import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { fmtDateTime } from '../../utils/region';
import Pagination from '../Pagination';
import type { AwCtx } from './ctx';
import { btn, card, dmy, errBox, note, pillGreen, pillGrey, sectionHead, table, td, th } from './ui';
import { emptyLine, Loader } from './dialog';
import { isReadOnlySeat } from './panes/fixedAssets/shared';

// ── Automatic posting, on two screens (owner 2026-11-02, "Automatically post accounting entries": limited; server/src/services/autoPost.ts) ──
//   AutoPostCard    Settings › Posting accounts: the switch for this client, and who turned it which way and when.
//   AutoPostStatus  Accounting › Journal entries: whether it is on, what is waiting, and the lines it could not make a draft of.
// Why a line that does have a draft stayed one is said on that draft's own card (Bookkeeping/DraftEntries.tsx, GET /journal/drafts).
// ⛔ Only an administrator is offered the switch (the server's `canTurn`, its own answer to who may). Anybody else, a read-only seat among them, sees the state and the trail and no switch at all, never one
// shown disabled: the server refuses the write, and a refusal pages the admins. Both reads answer 200, so nothing here can page anybody by being opened.
// ⛔ Off is the default, and turning it on changes no existing line and no existing draft. The confirmation says so, in those words.

type HoldRow = { lineId: string; docNo: string; date: string; section: string; code: string; message: string; savedBy: string; at: number };
type Status = {
  on: boolean; since: number; byName: string; at: number; waiting: number; hasVat?: boolean; available?: boolean; canTurn?: boolean;
  held: { rows: HoldRow[]; total: number; page: number; limit: number };
};
type LogPage = { rows: { on: boolean; byName: string; at: number }[]; total: number; page: number; limit: number };

export function AutoPostCard({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const readOnly = isReadOnlySeat(ctx);
  const [s, setS] = useState<Status | null>(null);
  const [log, setLog] = useState<LogPage | null>(null);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let alive = true;
    api.jnlAutoPost(ctx.clientId).then((d: Status) => { if (alive) setS(d); }).catch((e: any) => { if (alive) setErr(String(e?.message || e)); });
    api.jnlAutoPostLog(ctx.clientId, page).then((d: LogPage) => { if (alive) setLog(d); }).catch((e: any) => { if (alive) setErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, reload, page]);
  useEffect(() => { setPage(1); setErr(''); }, [ctx.clientId]);
  const turn = async (on: boolean) => {
    if (busy) return;
    if (!window.confirm(t(on ? 'aw_autopost_confirm_on' : 'aw_autopost_confirm_off'))) return;
    setBusy(true); setErr('');
    try { await api.jnlAutoPostSet(ctx.clientId, on); setPage(1); } catch (e: any) { setErr(String(e?.message || e)); } finally { setBusy(false); setReload(n => n + 1); }
  };
  if (!s) return err ? <div style={errBox}>{err}</div> : <Loader />;
  if (s.available === false) return null;   // not offered yet: server/src/services/autoPost.ts autoPostAvailable
  return (
    <section style={card} data-autopost-card={s.on ? 'on' : 'off'}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ ...sectionHead, marginBottom: 0, flex: 1 }}>{t('aw_autopost_title')}</div>
        <span style={s.on ? pillGreen : pillGrey} data-autopost-state>{t(s.on ? 'aw_autopost_on' : 'aw_autopost_off')}</span>
        {!readOnly && s.canTurn === true && (
          <button style={btn} disabled={busy} data-autopost-turn={s.on ? 'off' : 'on'} onClick={() => turn(!s.on)}>
            <i className={busy ? 'ti ti-loader' : 'ti ti-bolt'} aria-hidden /> {t(s.on ? 'aw_autopost_turn_off' : 'aw_autopost_turn_on')}
          </button>
        )}
      </div>
      <div style={{ ...note, marginTop: 6 }}>{t('aw_autopost_intro')}</div>
      <div style={{ ...note, marginTop: 4 }}>{t('aw_autopost_stays')}</div>
      <div style={{ ...note, marginTop: 4 }} data-autopost-limit>{t('aw_autopost_cannot_catch')}</div>
      {s.hasVat === false && <div style={{ ...note, marginTop: 4 }} data-autopost-no-vat>{t('aw_autopost_no_vat_client')}</div>}
      {err && <div style={{ ...errBox, marginTop: 8 }} data-autopost-error>{err}</div>}
      <div style={{ marginTop: 10 }} data-autopost-log>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>{t('aw_autopost_log_title')}</div>
        {!log ? <Loader /> : !log.rows.length ? <div style={emptyLine} data-autopost-log-empty>{t('aw_autopost_log_empty')}</div> : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={table}>
                <thead><tr><th style={th}>{t('aw_pacc_log_col_at')}</th><th style={th}>{t('aw_autopost_log_col_change')}</th><th style={th}>{t('aw_pacc_log_col_by')}</th></tr></thead>
                <tbody>
                  {log.rows.map((r, i) => (
                    <tr key={`${r.at}-${i}`} data-autopost-log-row={r.on ? 'on' : 'off'}>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDateTime(r.at)}</td>
                      <td style={td}>{t(r.on ? 'aw_autopost_turned_on' : 'aw_autopost_turned_off')}</td>
                      <td style={td}>{r.byName || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={log.page} total={log.total} limit={log.limit || 100} onPage={setPage} />
          </>
        )}
      </div>
    </section>
  );
}

export function AutoPostStatus({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [s, setS] = useState<Status | null>(null);
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [ctx.clientId]);
  useEffect(() => {
    let alive = true;
    // A strip that cannot be read says nothing rather than something false: the switch itself is unaffected.
    api.jnlAutoPost(ctx.clientId, page).then((d: Status) => { if (alive) setS(d); }).catch(() => { if (alive) setS(null); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, page]);
  if (!s || s.available === false) return null;
  return (
    <div data-autopost-status={s.on ? 'on' : 'off'} style={{ ...card, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-secondary)' }}>
        <i className="ti ti-bolt" style={{ color: s.on ? 'var(--accent)' : 'var(--text-tertiary)' }} aria-hidden />
        <span style={s.on ? pillGreen : pillGrey}>{t(s.on ? 'aw_autopost_on' : 'aw_autopost_off')}</span>
        <span style={{ minWidth: 0, flex: '1 1 260px' }}>
          {s.on
            ? t('aw_autopost_strip_on', { when: fmtDateTime(s.since), who: s.byName || '—' })
            : t('aw_autopost_strip_off')}
          {s.on && s.waiting > 0 && <span data-autopost-waiting={s.waiting}> {t('aw_autopost_waiting', { count: s.waiting })}</span>}
        </span>
      </div>
      {s.on && s.held.total > 0 && (
        <div data-autopost-held={s.held.total}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: '#92400E', marginBottom: 4 }}>{t('aw_autopost_held_title', { count: s.held.total })}</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={table}>
              <thead><tr>
                <th style={th}>{t('aw_autopost_held_doc')}</th><th style={th}>{t('aw_autopost_held_date')}</th>
                <th style={th}>{t('aw_autopost_held_why')}</th><th style={th}>{t('aw_autopost_held_saved')}</th>
              </tr></thead>
              <tbody>
                {s.held.rows.map(r => (
                  <tr key={r.lineId} data-autopost-held-row={r.code}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.docNo || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.date ? dmy(r.date) : '—'}</td>
                    <td style={td} title={r.message}>{t(`aw_autopost_hold_${r.code}`, { defaultValue: r.message })}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.savedBy || '—'} · {fmtDateTime(r.at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={s.held.page} total={s.held.total} limit={s.held.limit || 100} onPage={setPage} />
        </div>
      )}
    </div>
  );
}
